<template>
    <!-- <IndexBase @auth="onAuth($event)" action="Index" controller="Menu"></IndexBase> -->

    <Card>
        <template #title>
            <Toolbar>
                <template #left>
                    <div class="p-card-title">
                        {{ $t('menu') }}
                    </div>
                </template>
                <template #right>
                    <Button label="Expand All" icon="pi pi-angle-double-down" class="p-button-outlined p-button-sm mr-2"
                        @click="expandAll" />
                    <Button label="Collapse All" icon="pi pi-angle-double-up" class="p-button-outlined p-button-sm mr-2"
                        @click="collapseAll" />
                    <Button :label="$t('create')" icon="pi pi-plus"
                        class="p-button-primary p-button-sm" @click="openCreate(null)" />
                </template>
            </Toolbar>
        </template>
        <template #content>
            <TreeTable :value="treeNodes" :loading="loading" class="p-treetable-sm" v-model:expandedKeys="expandedKeys"
                responsiveLayout="scroll">
                <Column field="Menu_Name" :header="$t('menu_name')" :expander="true" style="min-width: 260px">
                    <template #body="{ node }">
                        <i v-if="node.data.Menu_Icon" :class="node.data.Menu_Icon" style="margin-right: 6px;"></i>
                        {{ node.data.Menu_Name }}
                    </template>
                </Column>
                <Column field="Menu_Controller" :header="$t('menu_controller')" style="min-width: 140px"></Column>
                <Column field="Menu_Action" :header="$t('action')" style="min-width: 120px"></Column>
                <Column field="Ordinal" :header="$t('ordinal')" style="min-width: 90px">
                    <template #body="{ node }">
                        {{ node.data.Ordinal }}
                    </template>
                </Column>
                <Column field="Visible" :header="$t('visible')" style="min-width: 90px">
                    <template #body="{ node }">
                        <Tag :severity="node.data.Visible ? 'success' : 'danger'"
                            :value="node.data.Visible ? 'Y' : 'N'"></Tag>
                    </template>
                </Column>
                <Column :exportable="false" headerStyle="white-space:nowrap; width: 180px">
                    <template #body="{ node }">
                        <Button icon="pi pi-search" class="p-button-rounded p-button-info mr-2 p-button-sm"
                            @click="openView(node.data)" />
                        <Button icon="pi pi-pencil"  class="p-button-rounded p-button-success mr-2 p-button-sm"
                            @click="openEdit(node.data)" />
                        <Button icon="pi pi-plus" class="p-button-rounded p-button-help mr-2 p-button-sm"
                            @click="openCreate(node.data.Id)" v-tooltip.top="$t('add_child')" />
                        <Button icon="pi pi-trash" class="p-button-rounded p-button-danger p-button-sm"
                            @click="confirmDelete(node.data)" />
                    </template>
                </Column>
                <template #empty>
                    <div class="p-3 text-center">{{ loading ? '' : 'No menu found' }}</div>
                </template>
            </TreeTable>
        </template>
    </Card>

    <Dialog :header="dialogTitle" v-model:visible="dialogVisible" :modal="true" :draggable="false"
        :style="{ width: '650px' }" :breakpoints="{ '960px': '90vw' }">
        <div class="p-inputtext-sm p-fluid" v-if="form">
            <div class="grid">
                <div class="col-8">
                    <div class="field grid">
                        <label class="col-4">{{ $t('menu_name') }}<span style="color: red;">*</span></label>
                        <div class="col-8">
                            <InputText v-model="form.Menu_Name" type="text" :placeholder="$t('menu_name')"
                                :class="{ 'p-invalid': submitted && !form.Menu_Name }" :disabled="readOnly" />
                            <small v-if="submitted && !form.Menu_Name" class="p-error">
                                {{ $t('is_required').replace('Value', $t('menu_name')) }}
                            </small>
                        </div>
                    </div>
                    <div class="field grid">
                        <label class="col-4">{{ $t('parent_menu') }}</label>
                        <div class="col-8">
                            <Dropdown v-model="form.Parent" :options="parentOptions" optionLabel="label"
                                optionValue="value" :filter="true" :disabled="readOnly" />
                        </div>
                    </div>
                    <div class="field grid">
                        <label class="col-4">{{ $t('menu_controller') }}</label>
                        <div class="col-8">
                            <InputText v-model="form.Menu_Controller" type="text" placeholder="e.g. Company"
                                :disabled="readOnly" />
                        </div>
                    </div>
                    <div class="field grid">
                        <label class="col-4">{{ $t('action') }}</label>
                        <div class="col-8">
                            <InputText v-model="form.Menu_Action" type="text" placeholder="e.g. Index"
                                :disabled="readOnly" />
                        </div>
                    </div>
                    <div class="field grid">
                        <label class="col-4">{{ $t('menu_icon') }}</label>
                        <div class="col-8" style="display:flex; align-items:center; gap:8px;">
                            <InputText v-model="form.Menu_Icon" type="text" placeholder="pi pi-fw pi-home"
                                :disabled="readOnly" />
                            <i v-if="form.Menu_Icon" :class="form.Menu_Icon" style="font-size: 1.3rem;"></i>
                        </div>
                    </div>
                    <div class="field grid">
                        <label class="col-4">{{ $t('ordinal') }}</label>
                        <div class="col-8">
                            <InputNumber v-model="form.Ordinal" :disabled="readOnly" />
                        </div>
                    </div>
                    <div class="field grid">
                        <label class="col-4">{{ $t('visible') }}</label>
                        <div class="col-8">
                            <ToggleButton v-model="form.Visible" onLabel="Yes" offLabel="No" :style="{ width: '8em' }"
                                :disabled="readOnly" />
                        </div>
                    </div>
                </div>
                <div class="col-4">
                    <div class="p-2" style="background: var(--surface-100); border-radius: 6px; height: 100%;">
                        <div style="font-weight: 600; margin-bottom: 6px;">{{ $t('existing_menu_here') }}</div>
                        <div v-if="siblingMenus.length == 0" style="opacity:.7;">-</div>
                        <div v-for="sibling in siblingMenus" :key="sibling.Id" style="display:flex; justify-content:space-between;">
                            <span>{{ sibling.Menu_Name }}</span>
                            <span style="opacity:.7;">{{ sibling.Ordinal }}</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <template #footer>
            <Button :label="$t('close')" icon="pi pi-times" class="p-button-outlined" @click="dialogVisible = false" />
            <Button v-if="dialogMode == this.$FORM_MODE_DELETE" :label="$t('delete')" icon="pi pi-trash"
                class="p-button-danger" @click="saveData()" />
            <Button v-else-if="!readOnly" :label="$t('save')" icon="pi pi-save" class="p-button-success"
                @click="validateAndSave()" />
        </template>
    </Dialog>
</template>

<script>
import IndexBase from '../Helper/IndexBase.vue'

export default {
    name: "Menu",
    components: {
        'IndexBase': IndexBase
    },
    data() {
        return {
            allMenus: [],
            loading: true,
            expandedKeys: {},
            url: this.$API_URL + "menu/",
            // canCreate: false,
            // canEdit: false,
            // canDelete: false,
            // canView: false,
            dialogVisible: false,
            dialogMode: "",
            submitted: false,
            form: null,
        }
    },
    methods: {
        onAuth(event) {
            // this.canCreate = event.canCreate;
            // this.canEdit = event.canEdit;
            // this.canDelete = event.canDelete;
            // this.canView = event.canView;
        },
        loadData() {
            this.loading = true;
            var self = this;

            this.$axios.get(this.url + "list")
                .then((response) => {
                    if (response.data.success) {
                        this.allMenus = response.data.data.map(m => {
                            m.Parent = m.Parent || 0;
                            return m;
                        });
                    }
                    else {
                        self.$toast.add({ severity: 'error', summary: "Error", detail: response.data.message, life: this.$DEFAULT_TIMER });
                    }
                    this.loading = false;
                })
                .catch(function (err) {
                    self.$toast.add({ severity: 'error', summary: "Error", detail: err.message, life: this.$DEFAULT_TIMER });
                    self.loading = false;
                });
        },
        getDescendantIds(id) {
            var result = [];
            var children = this.allMenus.filter(m => m.Parent == id);

            children.forEach(c => {
                result.push(c.Id);
                result = result.concat(this.getDescendantIds(c.Id));
            });

            return result;
        },
        nextOrdinal(parentId) {
            var normalizedParent = parentId || 0;
            var siblings = this.allMenus.filter(m => (m.Parent || 0) == normalizedParent);

            if (siblings.length == 0) return 10;

            var maxOrdinal = Math.max.apply(null, siblings.map(m => m.Ordinal ?? 0));
            return maxOrdinal + 10;
        },
        openCreate(parentId) {
            var normalizedParent = parentId || 0;

            this.dialogMode = this.$FORM_MODE_CREATE;
            this.submitted = false;
            this.form = {
                Id: 0,
                mode: this.$FORM_MODE_CREATE,
                Menu_Name: "",
                Menu_Action: "",
                Menu_Controller: "",
                Menu_Icon: "",
                Parent: normalizedParent,
                Ordinal: this.nextOrdinal(normalizedParent),
                Visible: true
            };
            this.dialogVisible = true;
        },
        getData(id, mode) {
            var self = this;
            this.$emit('block-ui');

            this.$axios.get(this.url + id + "/" + mode)
                .then((response) => {
                    this.$emit('unblock-ui');

                    if (response.data.success) {
                        self.form = response.data.data.header;
                        self.form.Parent = self.form.Parent || 0;
                        self.form.mode = mode;
                        self.dialogMode = mode;
                        self.submitted = false;
                        self.dialogVisible = true;
                    }
                    else {
                        self.$toast.add({ severity: 'error', summary: "Error", detail: response.data.message, life: this.$DEFAULT_TIMER });
                    }
                })
                .catch(function (error) {
                    self.$emit('unblock-ui');
                    self.$toast.add({ severity: 'error', summary: "Error", detail: error.message, life: this.$DEFAULT_TIMER });
                });
        },
        openEdit(data) {
            this.getData(data.Id, this.$FORM_MODE_EDIT);
        },
        openView(data) {
            this.getData(data.Id, this.$FORM_MODE_VIEW);
        },
        confirmDelete(data) {
            var self = this;

            this.$confirm.require({
                message: 'Are you sure you want to delete "' + data.Menu_Name + '"?',
                icon: 'pi pi-exclamation-triangle',
                accept: () => {
                    self.getData(data.Id, self.$FORM_MODE_DELETE);
                },
                reject: () => { }
            });
        },
        validateAndSave() {
            this.submitted = true;
            if (!this.form.Menu_Name) return;
            this.form.Parent = this.form.Parent || 0;
            this.saveData();
        },
        saveData() {
            this.$emit('block-ui');
            var self = this;

            this.$axios.post(this.url, this.form)
                .then((response) => {
                    self.$emit('unblock-ui');

                    if (response.data.success) {
                        self.dialogVisible = false;
                        self.loadData();
                        self.$toast.add({ severity: 'success', summary: 'Success', detail: 'Menu has been saved successfully', life: this.$DEFAULT_TIMER });
                    }
                    else {
                        self.$toast.add({ severity: 'error', summary: "Error Saving Data", detail: response.data.message, life: this.$DEFAULT_TIMER });
                    }
                })
                .catch(function (error) {
                    self.$emit('unblock-ui');
                    self.$toast.add({ severity: 'error', summary: "Error Saving Data", detail: error.message, life: this.$DEFAULT_TIMER });
                });
        },
        flattenForDropdown(nodes, depth, excludeIds, result) {
            nodes.forEach(node => {
                if (excludeIds.has(node.Id)) return;

                result.push({ label: "—".repeat(depth) + (depth > 0 ? " " : "") + node.Menu_Name, value: node.Id });
                var children = this.allMenus.filter(m => m.Parent == node.Id)
                    .sort((a, b) => (a.Ordinal ?? 0) - (b.Ordinal ?? 0));

                this.flattenForDropdown(children, depth + 1, excludeIds, result);
            });
        },
        expandAll() {
            var keys = {};
            this.allMenus.forEach(m => keys[m.Id] = true);
            this.expandedKeys = keys;
        },
        collapseAll() {
            this.expandedKeys = {};
        },
    },
    computed: {
        treeNodes() {
            var map = {};

            this.allMenus.forEach(m => {
                map[m.Id] = { key: m.Id.toString(), data: m, children: [] };
            });

            var roots = [];

            this.allMenus.forEach(m => {
                var node = map[m.Id];

                if (m.Parent && map[m.Parent]) {
                    map[m.Parent].children.push(node);
                } else {
                    roots.push(node);
                }
            });

            var sortRec = (nodes) => {
                nodes.sort((a, b) => (a.data.Ordinal ?? 0) - (b.data.Ordinal ?? 0) || (a.data.Menu_Name || "").localeCompare(b.data.Menu_Name || ""));
                nodes.forEach(n => sortRec(n.children));
            };

            sortRec(roots);

            return roots;
        },
        readOnly() {
            return this.dialogMode == this.$FORM_MODE_VIEW || this.dialogMode == this.$FORM_MODE_DELETE;
        },
        dialogTitle() {
            if (!this.dialogMode) return this.$t('menu');
            return "[" + this.dialogMode + "] " + this.$t('menu');
        },
        parentOptions() {
            var excludeIds = new Set();

            if (this.form && this.form.Id) {
                excludeIds.add(this.form.Id);
                this.getDescendantIds(this.form.Id).forEach(id => excludeIds.add(id));
            }

            var roots = this.allMenus.filter(m => !m.Parent)
                .sort((a, b) => (a.Ordinal ?? 0) - (b.Ordinal ?? 0));

            var result = [{ label: this.$t('top_level'), value: 0 }];
            this.flattenForDropdown(roots, 0, excludeIds, result);

            return result;
        },
        siblingMenus() {
            if (!this.form) return [];

            var normalizedParent = this.form.Parent || 0;

            return this.allMenus
                .filter(m => (m.Parent || 0) == normalizedParent && m.Id != this.form.Id)
                .sort((a, b) => (a.Ordinal ?? 0) - (b.Ordinal ?? 0));
        }
    },
    mounted() {
        this.$emit('unblock-ui');
        this.loadData();
        this.$showToast();
    },
}
</script>

<style scoped>
.p-card .p-card-content {
    margin: 0 !important;
    padding: 0 !important;
}
</style>
